import { lazy } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PortalsName } from 'utils/enums';

import { ArticlesDetails } from 'components';
import * as Pages from 'containers/pages';

const InvestorHome = lazy(() => import('containers/pages/investor-home'));
const InvestorBankAccounts = lazy(
  () => import('containers/pages/investor-bank-accounts')
);
const InvestorBalance = lazy(() => import('containers/pages/investor-balance'));
const InvestorNetIncome = lazy(() => import('containers/pages/net-income'));
const InvestorSpace = lazy(() => import('containers/pages/investor-space'));
const InvestorLiabilities = lazy(
  () => import('containers/pages/investor-liabilities')
);
const InvestorTenants = lazy(() => import('containers/pages/investor-tenants'));

const FinancialInformationPage = lazy(
  () => import('containers/pages/financial-information')
);

const FiscalYearDocumentsPage = lazy(
  () => import('containers/pages/fiscal-year-documents')
);
const TodoPage = lazy(() => import('containers/pages/todo'));
const ServicesCalendarPage = lazy(() => import('containers/services-calendar'));
const MaintenanceDashboardPage = lazy(
  () => import('containers/pages/maintenance-dashboard')
);
const RescuePlanPage = lazy(() => import('containers/pages/rescue-plan'));
const EventsInformationPage = lazy(
  () => import('containers/pages/events-information')
);
const MaintenanceTasksPage = lazy(() => import('containers/pages/tasks'));
const MaintenanceTaskPage = lazy(() => import('containers/pages/task'));

const CooperativesPage = lazy(() => import('pages/cooperatives'));
const CooperativeCommonCasesPage = lazy(() => import('pages/common-cases'));
const CooperativeSpacesPage = lazy(() => import('pages/cooperative-spaces'));
const DocumentsPage = lazy(() => import('pages/documents'));
const EventsListPage = lazy(() => import('pages/events-list'));
const IncidentPage = lazy(() => import('pages/incident'));
const InvoicesPage = lazy(() => import('pages/invoices'));
const KeyOrdersPage = lazy(() => import('pages/key-orders'));
const LotsPage = lazy(() => import('pages/lots'));
const MassMessagesPage = lazy(() => import('pages/mass-messages/MassMessages'));
const MeterReadingPage = lazy(() => import('pages/meter-reading'));
const NewslettersPage = lazy(() => import('pages/newsletters/ui'));
const PrivacyPolicyPage = lazy(() => import('pages/privacy-policy'));
const RepairPage = lazy(() => import('pages/repair'));
const SpaceRenovationRequestsPage = lazy(
  () => import('pages/space-renovation')
);
const TechnicalSubjectsPage = lazy(() => import('pages/technical-subjects'));
const UserSpacesPage = lazy(() => import('pages/user-spaces'));

export enum Route {
  Hidden,
  Visible,
}

export interface RouteProps {
  id: string;
  type: Route.Hidden | Route.Visible;
  path: string;
  portalsVisibility: PortalsName[];
  exact?: boolean;
  href?: string;
  render?: (props: RouteComponentProps<any>) => React.ReactNode;
}

export const routes: RouteProps[] = [
  {
    path: '/',
    id: 'home',
    type: Route.Visible,
    render: () => <Pages.MainPage />,
    exact: true,
    portalsVisibility: [PortalsName.Default, PortalsName.Maintenance],
  },
  {
    path: '/cooperatives',
    render: () => <CooperativesPage />,
    exact: true,
    id: 'cooperatives',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/cooperative',
    render: () => null,
    exact: true,
    id: 'cooperative',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/lots',
    render: () => <LotsPage />,
    exact: true,
    id: 'lots',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/buildings',
    render: () => <Pages.Buildings />,
    exact: true,
    id: 'buildings',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/online-store',
    render: () => <Pages.Catalog />,
    exact: true,
    id: 'online-store',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/board-eventsinformation/:id?',
    render: () => <EventsInformationPage forBoardMemebers />,
    exact: true,
    id: 'board-eventsinformation',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/articles',
    render: () => <ArticlesDetails />,
    exact: true,
    id: 'articles',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/rescue-plan',
    render: () => <RescuePlanPage />,
    exact: true,
    id: 'rescue-plan',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/fiscal-year-materials',
    render: () => null,
    exact: true,
    id: 'fiscal-year-materials',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/finance-auditor',
    render: () => null,
    exact: true,
    id: 'finance-auditor',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/technical-products',
    render: () => null,
    exact: true,
    id: 'technical-products',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/finance',
    render: () => null,
    exact: true,
    id: 'finance',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/investor-service',
    render: () => null,
    exact: true,
    id: 'investor-service',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/releases',
    render: () => null,
    exact: true,
    id: 'releases',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/user-spaces',
    render: () => <UserSpacesPage />,
    exact: true,
    id: 'user-spaces',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/consumption',
    render: () => <Pages.Consumption />,
    exact: true,
    id: 'consumption',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/spaces/:id?',
    render: () => <CooperativeSpacesPage />,
    id: 'spaces',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/service-providers',
    render: () => <Pages.ServiceProviders />,
    exact: true,
    id: 'service-providers',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/balance',
    render: () => <Pages.Balance />,
    exact: true,
    id: 'balance',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/balanceloan',
    render: () => <Pages.BalanceLoan />,
    exact: true,
    id: 'balanceloan',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/key-order/:id?',
    render: () => <KeyOrdersPage />,
    id: 'key-order',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/incident/:id?',
    render: () => <IncidentPage />,
    id: 'incident',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/repair/:id?',
    render: () => <RepairPage />,
    exact: true,
    id: 'repair',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/person-information',
    render: () => <Pages.PersonInformation />,
    exact: true,
    id: 'person-information',
    type: Route.Visible,
    portalsVisibility: [
      PortalsName.Default,
      PortalsName.Investor,
      PortalsName.Maintenance,
    ],
  },
  {
    path: '/vote-register',
    render: () => <Pages.VoteRegister />,
    exact: true,
    id: 'vote-register',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/share-register',
    render: () => <Pages.ShareRegister />,
    exact: true,
    id: 'share-register',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/contact-information',
    render: () => <Pages.ContactInformation />,
    exact: true,
    id: 'contact-information',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/services',
    render: () => <Pages.Services />,
    exact: true,
    id: 'services',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/services/:id',
    exact: true,
    render: () => <ServicesCalendarPage />,
    id: 'services',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/importtool',
    exact: true,
    render: () => <Pages.Importtool />,
    id: 'importtool',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/bank-accounts',
    exact: true,
    render: () => <Pages.BankAccounts />,
    id: 'bank-accounts',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/space-renovation/:id?',
    exact: true,
    render: () => <SpaceRenovationRequestsPage />,
    id: 'space-renovation',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/eventsinformation/:id?',
    exact: true,
    render: () => <EventsInformationPage />,
    id: 'eventsinformation',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/cooperative-budget',
    exact: true,
    render: () => <Pages.CooperativeBudget />,
    id: 'cooperative-budget',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/cooperative-balance',
    exact: true,
    render: () => <Pages.CooperativeBalance />,
    id: 'cooperative-balance',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/incoming-invoices/:id?',
    exact: true,
    render: () => <Pages.PurchaseInvoices />,
    id: 'incoming-invoices',
    type: Route.Hidden,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/todo/:id?',
    exact: true,
    render: () => <TodoPage />,
    id: 'todo',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/common-cases/:id?',
    exact: true,
    render: () => <CooperativeCommonCasesPage />,
    id: 'common-cases',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/constructors-common-cases/:id?',
    exact: true,
    render: () => <CooperativeCommonCasesPage />,
    id: 'constructors-common-cases',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/documents',
    exact: true,
    render: () => <DocumentsPage />,
    id: 'documents',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/invoices',
    exact: true,
    render: () => <InvoicesPage />,
    id: 'invoices',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/privacy-policy',
    exact: true,
    render: () => <PrivacyPolicyPage />,
    id: 'privacy-policy',
    type: Route.Visible,
    portalsVisibility: [
      PortalsName.Default,
      PortalsName.Investor,
      PortalsName.Maintenance,
    ],
  },
  {
    path: '/mass-messages/:id?',
    exact: true,
    render: () => <MassMessagesPage />,
    id: 'mass-messages',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/project',
    exact: true,
    render: () => <TechnicalSubjectsPage />,
    id: 'project',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/constructors-mass-messages/:id?',
    exact: true,
    render: () => <MassMessagesPage />,
    id: 'constructors-mass-messages',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/event-list/:id?',
    exact: true,
    render: () => <EventsListPage />,
    id: 'event-list',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/newsletters/:id?',
    exact: true,
    render: () => <NewslettersPage />,
    id: 'newsletters',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/fiscal-year-documents',
    exact: true,
    render: () => <FiscalYearDocumentsPage />,
    id: 'fiscal-year-documents',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/financial-information',
    exact: true,
    render: () => <FinancialInformationPage />,
    id: 'financial-information',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/documents-order',
    href: 'https://kauppa.asiakirjatilaus.fi/kiinteistotahkola/51476fd0-d958-40da-93eb-93828072fe9b',
    exact: true,
    id: 'documents-order',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Default],
  },
  {
    path: '/investor',
    exact: true,
    render: () => <InvestorHome />,
    id: 'investor',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/bank-accounts',
    exact: true,
    render: () => <InvestorBankAccounts />,
    id: 'investor/bank-accounts',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/cooperatives',
    exact: true,
    render: () => null,
    id: 'investor/cooperatives',
    type: Route.Hidden,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/net-income',
    exact: true,
    render: () => <InvestorNetIncome />,
    id: 'investor/net-income',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/investor-balance',
    exact: true,
    render: () => <InvestorBalance />,
    id: 'investor/investor-balance',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/tenants',
    exact: true,
    render: () => <InvestorTenants />,
    id: 'investor/tenants',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/liabilities',
    exact: true,
    render: () => <InvestorLiabilities />,
    id: 'investor/liabilities',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/investor/spaces',
    exact: true,
    render: () => <InvestorSpace />,
    id: 'investor/spaces',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Investor],
  },
  {
    path: '/maintenance/cooperatives',
    exact: true,
    render: () => <CooperativesPage />,
    id: 'maintenance/cooperatives',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/contact-information',
    render: () => (
      <Pages.ContactInformation entity="maintenance/contact-information" />
    ),
    exact: true,
    id: 'maintenance/contact-information',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/buildings',
    exact: true,
    render: () => <Pages.Buildings />,
    id: 'maintenance/buildings',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/lots',
    exact: true,
    render: () => <LotsPage />,
    id: 'maintenance/lots',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/spaces/:id?',
    render: () => <CooperativeSpacesPage />,
    id: 'maintenance/spaces',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/service-providers',
    exact: true,
    render: () => <Pages.ServiceProviders />,
    id: 'maintenance/service-providers',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/mass-messages/:id?',
    exact: true,
    render: () => <MassMessagesPage />,
    id: 'maintenance/mass-messages',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/event-list/:id?',
    exact: true,
    render: () => <EventsListPage />,
    id: 'maintenance/event-list',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/booking-services',
    exact: true,
    render: () => <Pages.Services />,
    id: 'maintenance/booking-services',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/booking-services/:id',
    exact: true,
    render: () => <ServicesCalendarPage />,
    id: 'maintenance/booking-services',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/meter-reading',
    exact: true,
    render: () => <MeterReadingPage />,
    id: 'maintenance/meter-reading',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/dashboard',
    exact: true,
    render: () => <MaintenanceDashboardPage />,
    id: 'maintenance/dashboard',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/dashboard/:id',
    exact: true,
    render: () => <MaintenanceTaskPage />,
    id: 'maintenance/dashboard',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/documents',
    exact: true,
    render: () => <DocumentsPage />,
    id: 'maintenance/documents',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/tasks',
    exact: true,
    render: () => <MaintenanceTasksPage />,
    id: 'maintenance/tasks',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/tasks/:id',
    exact: true,
    render: () => <MaintenanceTaskPage />,
    id: 'maintenance/tasks',
    type: Route.Visible,
    portalsVisibility: [PortalsName.Maintenance],
  },
  {
    path: '/maintenance/articles',
    exact: true,
    render: () => null,
    id: 'maintenance/articles',
    type: Route.Hidden,
    portalsVisibility: [PortalsName.Maintenance],
  },
];
